// Run checkpoints and the run worklog (docs/plans/2026-10-02-checkpoints-en-overdracht.md
// §3.2/§3.5, contract docs/plans/2026-10-02-checkpoints-contract.md §3.2).

import { z } from 'zod'

/**
 * `run_checkpoints.kind`:
 * - `periodic`: written while the run works (every 5 min, after report_progress/finish/commit), one per run, overwritten.
 * - `pause`: an `ask_human`/`request_approval`/guard-ask wait past 15 min (the old `run_pauses` rows).
 * - `failure`: written before the sandbox goes away on any outcome but `completed` (and on quota/auth).
 * - `handoff`: the checkpoint a child run took over; the API relabels the source checkpoint to this kind when it hands off.
 * Every kind but `periodic` is kept (until the retention sweep).
 */
export const RunCheckpointKindSchema = z.enum(['periodic', 'pause', 'failure', 'handoff'])
export type RunCheckpointKind = z.infer<typeof RunCheckpointKindSchema>
export const RUN_CHECKPOINT_KINDS: readonly RunCheckpointKind[] = RunCheckpointKindSchema.options

/** `run_checkpoints.reason` for kind `pause` (null for every other kind). */
export const RunPauseReasonSchema = z.enum(['ask_human', 'request_approval', 'guard_ask'])
export type RunPauseReason = z.infer<typeof RunPauseReasonSchema>

/** What made the worker write a checkpoint (timeline + debugging only). */
export const CheckpointTriggerSchema = z.enum([
  'interval',
  'report_progress',
  'finish',
  'commit',
  'pause',
  'failure',
  'shutdown',
])
export type CheckpointTrigger = z.infer<typeof CheckpointTriggerSchema>

/** How often the worker takes a periodic checkpoint (only when diff hash or session size changed). */
export const CHECKPOINT_INTERVAL_MS = 5 * 60_000
/** A crashed run (lease expired) is resumed from its last checkpoint at most this many times, then `failed`. */
export const MAX_CRASH_RESUMES = 2
/** Checkpoints of a run in a final status are wiped this long after they were written (same 7 days as a pause). */
export const RUN_CHECKPOINT_RETENTION_SECONDS = 7 * 24 * 3600
/** Where Pi keeps its session inside the sandbox once it runs with `--session` (design §3.2). */
export const PI_SESSION_FILE = '/tmp/.pi/session.jsonl'
/** Where a handoff's full transcript of the previous run(s) is written inside the successor's sandbox (design §3.5). */
export const HANDOFF_TRANSCRIPT_PATH = '/tmp/.goblic/handoff/transcript.md'

// ---------------------------------------------------------------------------
// Worklog — what `report_progress` adds to (design §3.5 "Werklog")
// ---------------------------------------------------------------------------

export const WORKLOG_MAX_ITEMS = 20
export const WORKLOG_ITEM_MAX_LENGTH = 500

const WorklogItemsSchema = z.array(z.string().trim().min(1).max(WORKLOG_ITEM_MAX_LENGTH)).max(WORKLOG_MAX_ITEMS)

/**
 * The run's latest worklog, kept by the worker and stored on every
 * checkpoint (`run_checkpoints.worklog`). A child run starts from its
 * parent's. Lists are *replaced* (not appended) by a `report_progress` that
 * sends them; a call that omits a list keeps the previous one
 * (`mergeWorklog`).
 */
export const RunWorklogSchema = z.object({
  /** Last `report_progress.message`. */
  message: z.string(),
  percent: z.number().min(0).max(100).nullable().default(null),
  done: WorklogItemsSchema.default([]),
  next: WorklogItemsSchema.default([]),
  decisions: WorklogItemsSchema.default([]),
  /** ISO time of the last update. */
  updatedAt: z.string().datetime({ offset: true }),
})
export type RunWorklog = z.infer<typeof RunWorklogSchema>

/** The `report_progress` input fields the worklog takes (see run-tools/catalog.ts `ReportProgressInputSchema`). */
export interface WorklogUpdate {
  message: string
  percent?: number
  done?: string[]
  next?: string[]
  decisions?: string[]
}

/** Fold one `report_progress` call into the previous worklog (pure). Lists are capped at `WORKLOG_MAX_ITEMS`, keeping the last ones. */
export function mergeWorklog(previous: RunWorklog | null | undefined, update: WorklogUpdate, now: Date): RunWorklog {
  const cap = (items: string[] | undefined, fallback: string[]): string[] =>
    (items ?? fallback).map((item) => item.trim().slice(0, WORKLOG_ITEM_MAX_LENGTH)).filter(Boolean).slice(-WORKLOG_MAX_ITEMS)
  return {
    message: update.message,
    percent: update.percent ?? previous?.percent ?? null,
    done: cap(update.done, previous?.done ?? []),
    next: cap(update.next, previous?.next ?? []),
    decisions: cap(update.decisions, previous?.decisions ?? []),
    updatedAt: now.toISOString(),
  }
}
